import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { Modal } from '@/components/ui/modal';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertCircle,
  CalendarX,
  CheckCircle2,
  Eye,
  FileText,
  Info,
  PencilLine,
  RotateCcw,
  Scale,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/hooks/useI18n';
import { toast } from '@/hooks/useToast';
import type { TranslationKey } from '@/types/i18n.types';
import { useLegalSettings } from './hooks/useLegalSettings';
import { LegalMembersCard } from './LegalMembersCard';
import {
  ILegalDocumentState,
  ILegalSettings,
  ILegalSettingsForm,
  LegalDocumentKey,
} from '@/lib/legal/legal.model';
import { LegalAcceptance } from '@/lib/legal/LegalAcceptance';

const EMPTY: ILegalSettings = {
  legal_name: '',
  legal_email: '',
  legal_address: '',
  tax_id: '',
  cancellation_policy: '',
  privacy_policy: '',
  terms_conditions: '',
};

// Ordine dei campi nella pagina: diverso da LEGAL_DOCUMENT_ORDER, che e' quello in cui
// il cliente li legge nella riga di accettazione.
const DOCUMENT_KEYS: LegalDocumentKey[] = [
  'cancellation_policy',
  'privacy_policy',
  'terms_conditions',
];

const DOCUMENT_LABEL_KEY: Record<LegalDocumentKey, TranslationKey> = {
  cancellation_policy: 'legalSettings.documents.cancellationPolicy',
  privacy_policy: 'legalSettings.documents.privacyPolicy',
  terms_conditions: 'legalSettings.documents.termsConditions',
};

const DOCUMENT_DEFAULT_KEY: Record<LegalDocumentKey, TranslationKey> = {
  cancellation_policy: 'legalSettings.defaults.cancellationPolicy',
  privacy_policy: 'legalSettings.defaults.privacyPolicy',
  terms_conditions: 'legalSettings.defaults.termsConditions',
};

const DOCUMENT_ICON: Record<LegalDocumentKey, LucideIcon> = {
  cancellation_policy: CalendarX,
  privacy_policy: ShieldCheck,
  terms_conditions: FileText,
};

const DETAIL_FIELDS = ['legal_name', 'legal_email', 'legal_address', 'tax_id'] as const;
type LegalField = (typeof DETAIL_FIELDS)[number] | LegalDocumentKey;
const ALL_FIELDS: readonly LegalField[] = [...DETAIL_FIELDS, ...DOCUMENT_KEYS];

/** Ogni card si salva da sola: l'anagrafica oppure uno dei documenti. */
type LegalSection = 'details' | LegalDocumentKey;
const sectionFields = (section: LegalSection): readonly LegalField[] =>
  section === 'details' ? DETAIL_FIELDS : [section];

const pickFields = (source: ILegalSettings | undefined, fields: readonly LegalField[]) =>
  Object.fromEntries(fields.map((field) => [field, source?.[field] ?? ''])) as ILegalSettings;

/** Per il membro: testo personalizzato, oppure null = eredita dall'agenzia. */
type Overrides = Record<LegalDocumentKey, string | null>;

/**
 * Dati e testi legali che il cliente si vede prima di pagare.
 *
 * Due modalita', decise dal backend:
 * - agenzia / advisor autonomo (`can_edit`): anagrafica e documenti sono suoi;
 * - membro del team (`can_override`): l'anagrafica resta quella dell'agenzia — e' di
 *   chi incassa — mentre ogni documento si puo' ereditare o personalizzare. Se l'agenzia
 *   riscrive un documento personalizzato, il membro lo trova segnato "da aggiornare".
 */
const LegalPage = () => {
  const { t, language } = useI18n();
  const {
    settings,
    isLoading,
    save,
    saveOverrides,
    requestMemberUpdate,
    isRequestingMemberUpdate,
  } = useLegalSettings();

  const [form, setForm] = useState<ILegalSettings>(EMPTY);
  const [overrides, setOverrides] = useState<Overrides>({
    cancellation_policy: null,
    privacy_policy: null,
    terms_conditions: null,
  });
  // Documenti che il membro ha toccato (modificato, ripristinato o segnato come rivisto):
  // sono gli unici da mandare, perche' mandarne uno ne aggiorna la versione di riferimento.
  const [touched, setTouched] = useState<Set<LegalDocumentKey>>(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [previewAccepted, setPreviewAccepted] = useState(false);
  const [agencyVersionOf, setAgencyVersionOf] = useState<LegalDocumentKey | null>(null);
  const [savingSection, setSavingSection] = useState<LegalSection | null>(null);
  const lastSettings = useRef<ILegalSettingsForm>();

  const canEdit = settings?.can_edit === true;
  const canOverride = settings?.can_override === true;

  // Salvare una card fa tornare dal server l'intero record: i campi con modifiche non
  // ancora salvate (nelle altre card) restano come sono, il resto si riallinea al server.
  useEffect(() => {
    if (!settings) return;
    const previous = lastSettings.current;
    lastSettings.current = settings;
    setForm((current) => {
      const next: ILegalSettings = {};
      ALL_FIELDS.forEach((field) => {
        const pending = !!previous && (current[field] ?? '') !== (previous[field] ?? '');
        next[field] = pending ? current[field] : (settings[field] ?? '');
      });
      return next;
    });
    setOverrides((current) => {
      const next = { ...current };
      DOCUMENT_KEYS.forEach((key) => {
        if (!touched.has(key)) next[key] = overriddenValue(settings.documents?.[key]);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const update = (patch: Partial<ILegalSettings>) => setForm((prev) => ({ ...prev, ...patch }));

  const setOverride = (key: LegalDocumentKey, value: string | null) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => new Set(prev).add(key));
  };

  const markReviewed = (key: LegalDocumentKey) => setTouched((prev) => new Set(prev).add(key));

  const agencyValue = (key: LegalDocumentKey) => settings?.documents?.[key]?.agency_value ?? '';

  // Testi che vedrebbe il cliente con le modifiche non ancora salvate: per il membro la
  // sua versione dove c'e', altrimenti quella dell'agenzia.
  const effective: ILegalSettings = useMemo(() => {
    if (!canOverride) return form;
    return {
      ...form,
      cancellation_policy: overrides.cancellation_policy ?? agencyValue('cancellation_policy'),
      privacy_policy: overrides.privacy_policy ?? agencyValue('privacy_policy'),
      terms_conditions: overrides.terms_conditions ?? agencyValue('terms_conditions'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOverride, form, overrides, settings]);

  const hasAnyDocument = !!(
    effective.cancellation_policy ||
    effective.privacy_policy ||
    effective.terms_conditions
  );

  const isSectionDirty = (section: LegalSection) => {
    if (!settings) return false;
    if (canOverride && section !== 'details') return touched.has(section);
    return sectionFields(section).some((field) => (form[field] ?? '') !== (settings[field] ?? ''));
  };

  const handleRestoreDefault = (key: LegalDocumentKey) =>
    update({ [key]: t(DOCUMENT_DEFAULT_KEY[key]) });

  // Salva solo la card indicata. L'agenzia ha una RPC che scrive l'intero record: le
  // altre card ci vanno con il valore gia' salvato, non con le modifiche in sospeso.
  const handleSave = async (section: LegalSection) => {
    setSavingSection(section);
    try {
      if (canOverride && section !== 'details') {
        await saveOverrides({ [section]: overrides[section] });
        setTouched((prev) => {
          const next = new Set(prev);
          next.delete(section);
          return next;
        });
      } else {
        await save({
          ...pickFields(settings, ALL_FIELDS),
          ...pickFields(form, sectionFields(section)),
        });
      }
      toast({ variant: 'success', title: t('legalSettings.saved') });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: t('legalSettings.saveError'),
        description: error?.message,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const saveProps = (section: LegalSection): CardSaveProps => ({
    dirty: isSectionDirty(section),
    saving: savingSection === section,
    busy: savingSection !== null,
    onSave: () => handleSave(section),
  });

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(language) : '';

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {settings?.inherited && (
        <Alert variant="info">
          <Info className="h-4 w-4" />
          <AlertDescription>
            {t('legalSettings.inheritedNotice', {
              agency: settings.owner_name ?? '',
            })}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-4 flex flex-col">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Scale className="h-4 w-4 text-primary" />
              {t('legalSettings.title')}
              {settings?.inherited && (
                <Badge variant="secondary">{t('legalSettings.inheritedBadge')}</Badge>
              )}
            </CardTitle>
            <p className="text-sm text-gray-600">{t('legalSettings.description')}</p>
            <button
              type="button"
              className="text-sm underline text-gray-700 hover:text-gray-900 self-start"
              onClick={() => setShowPreview(true)}
            >
              <Eye className="inline h-4 w-4 mr-1" />
              {t('legalSettings.previewLink')}
            </button>
          </CardHeader>

          <CardContent className="flex flex-1 flex-col gap-3 p-4 pt-0">
            {(canEdit || canOverride) && !hasAnyDocument && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{t('legalSettings.incompleteWarning')}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="legal_name">{t('legalSettings.fields.legalName')}</Label>
                <Input
                  id="legal_name"
                  value={form.legal_name ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => update({ legal_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="legal_email">{t('legalSettings.fields.legalEmail')}</Label>
                <Input
                  id="legal_email"
                  type="email"
                  value={form.legal_email ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => update({ legal_email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="legal_address">{t('legalSettings.fields.legalAddress')}</Label>
                <Textarea
                  id="legal_address"
                  rows={2}
                  value={form.legal_address ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => update({ legal_address: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tax_id">{t('legalSettings.fields.taxId')}</Label>
                <Input
                  id="tax_id"
                  value={form.tax_id ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => update({ tax_id: e.target.value })}
                />
              </div>
            </div>

            {/* In fondo alla card, che si allunga fino all'altezza della colonna dei testi. */}
            {canEdit && <CardSaveButton className="mt-auto pt-2" {...saveProps('details')} />}
          </CardContent>
        </Card>

        {/* Seconda colonna: i documenti uno sotto l'altro. */}
        <div className="space-y-4 lg:col-span-8">
          {DOCUMENT_KEYS.map((key) =>
            canOverride ? (
              <MemberDocumentField
                key={key}
                icon={DOCUMENT_ICON[key]}
                label={t(DOCUMENT_LABEL_KEY[key])}
                state={settings?.documents?.[key]}
                override={overrides[key]}
                agencyValue={agencyValue(key)}
                touched={touched.has(key)}
                formatDate={formatDate}
                onCustomize={() => setOverride(key, agencyValue(key))}
                onChange={(value) => setOverride(key, value)}
                onRestore={() => setOverride(key, null)}
                onMarkReviewed={() => markReviewed(key)}
                onShowAgencyVersion={() => setAgencyVersionOf(key)}
                save={saveProps(key)}
              />
            ) : (
              <LegalDocumentField
                key={key}
                icon={DOCUMENT_ICON[key]}
                label={t(DOCUMENT_LABEL_KEY[key])}
                value={form[key] ?? ''}
                readOnly={!canEdit}
                onChange={(value) => update({ [key]: value })}
                onRestoreDefault={() => handleRestoreDefault(key)}
                save={saveProps(key)}
              />
            ),
          )}
        </div>
      </div>

      {canEdit && settings?.members && settings.members.length > 0 && (
        <LegalMembersCard
          members={settings.members}
          onRequestUpdate={requestMemberUpdate}
          isRequesting={isRequestingMemberUpdate}
        />
      )}

      {/* Anteprima: la stessa riga di accettazione che vede il cliente, montata sui
          testi non ancora salvati. Vedere il proprio testo dentro il componente vero
          e' l'unico modo per accorgersi che un documento vuoto sparisce dall'elenco. */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title={t('legalSettings.previewTitle')}
        size="md"
      >
        {/* Il padding del contenuto lo mette il chiamante, non la Modal. */}
        <div className="space-y-3 px-6 py-4">
          <p className="text-sm text-gray-600">{t('legalSettings.previewIntro')}</p>
          <LegalAcceptance
            legal={effective}
            checked={previewAccepted}
            onCheckedChange={setPreviewAccepted}
          />
          <Button className="w-full" disabled={!previewAccepted}>
            {t('sharedProposal.payNow')}
          </Button>
        </div>
      </Modal>

      {/* Versione attuale dell'agenzia, per il membro che deve riallineare la propria. */}
      <Modal
        isOpen={!!agencyVersionOf}
        onClose={() => setAgencyVersionOf(null)}
        title={agencyVersionOf ? t(DOCUMENT_LABEL_KEY[agencyVersionOf]) : ''}
        size="lg"
      >
        <div className="space-y-4 px-6 py-4">
          <p className="text-sm text-gray-600">
            {t('legalSettings.member.agencyVersionIntro', {
              agency: settings?.owner_name ?? '',
              date: formatDate(
                agencyVersionOf ? settings?.documents?.[agencyVersionOf]?.agency_updated_at : null,
              ),
            })}
          </p>
          <div className="max-h-[50vh] overflow-y-auto rounded-md border border-gray-200 p-4">
            <RichTextContent html={agencyVersionOf ? agencyValue(agencyVersionOf) : null} />
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                if (agencyVersionOf) setOverride(agencyVersionOf, agencyValue(agencyVersionOf));
                setAgencyVersionOf(null);
              }}
            >
              <PencilLine className="h-4 w-4" />
              {t('legalSettings.member.startFromAgency')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const overriddenValue = (state?: ILegalDocumentState) =>
  state?.overridden ? (state.value ?? '') : null;

const DocumentCardTitle = ({ icon: Icon, label }: { icon: LucideIcon; label: string }) => (
  <CardTitle className="flex items-center gap-2 text-base">
    <Icon className="h-4 w-4 text-primary" />
    {label}
  </CardTitle>
);

type CardSaveProps = {
  dirty: boolean;
  /** Questa card sta salvando. */
  saving: boolean;
  /** Una qualsiasi card sta salvando: una sola scrittura alla volta. */
  busy: boolean;
  onSave: () => void;
};

const CardSaveButton = ({
  dirty,
  saving,
  busy,
  onSave,
  className,
}: CardSaveProps & { className?: string }) => {
  const { t } = useI18n();

  return (
    <div className={cn('flex justify-end', className)}>
      <Button size="sm" onClick={onSave} disabled={!dirty || busy}>
        {saving ? t('common.saving') : t('legalSettings.save')}
      </Button>
    </div>
  );
};

// Card compatte in griglia: i testi lunghi scorrono dentro l'editor invece di
// allungare la card e sbilanciare le due colonne.
const DOCUMENT_EDITOR_CLASS = 'text-sm max-h-72 overflow-y-auto';

const LegalDocumentField = ({
  icon,
  label,
  value,
  readOnly,
  onChange,
  onRestoreDefault,
  save,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  readOnly: boolean;
  onChange: (value: string) => void;
  onRestoreDefault: () => void;
  save: CardSaveProps;
}) => {
  const { t } = useI18n();

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <DocumentCardTitle icon={icon} label={label} />
          {!readOnly && (
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={onRestoreDefault}>
              <RotateCcw className="h-4 w-4" />
              {t('legalSettings.restoreDefault')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0">
        <RichTextEditor
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          className={DOCUMENT_EDITOR_CLASS}
        />
        {!readOnly && <CardSaveButton {...save} />}
      </CardContent>
    </Card>
  );
};

/**
 * Un documento visto dal membro del team: ereditato (sola lettura, "Personalizza")
 * oppure personalizzato (modificabile, "Ripristina testo dell'agenzia").
 */
const MemberDocumentField = ({
  icon,
  label,
  state,
  override,
  agencyValue,
  touched,
  formatDate,
  onCustomize,
  onChange,
  onRestore,
  onMarkReviewed,
  onShowAgencyVersion,
  save,
}: {
  icon: LucideIcon;
  label: string;
  state?: ILegalDocumentState;
  override: string | null;
  agencyValue: string;
  touched: boolean;
  formatDate: (value?: string | null) => string;
  onCustomize: () => void;
  onChange: (value: string) => void;
  onRestore: () => void;
  onMarkReviewed: () => void;
  onShowAgencyVersion: () => void;
  save: CardSaveProps;
}) => {
  const { t } = useI18n();
  const isOverridden = override !== null;
  // "Da aggiornare" e' uno stato salvato: sparisce appena il membro tocca il documento,
  // perche' salvarlo lo riallinea alla versione attuale dell'agenzia.
  const showOutdated = isOverridden && state?.outdated && !touched;

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <DocumentCardTitle icon={icon} label={label} />
            {isOverridden ? (
              <Badge variant="outline">{t('legalSettings.member.customizedBadge')}</Badge>
            ) : (
              <Badge variant="secondary">{t('legalSettings.inheritedBadge')}</Badge>
            )}
          </div>
          {isOverridden ? (
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={onRestore}>
              <RotateCcw className="h-4 w-4" />
              {t('legalSettings.member.restoreAgency')}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={onCustomize}>
              <PencilLine className="h-4 w-4" />
              {t('legalSettings.member.customize')}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3 p-4 pt-0">
        {showOutdated && (
          <Alert variant="warning">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="space-y-2">
              <p>
                {t('legalSettings.member.outdatedNotice', {
                  date: formatDate(state?.agency_updated_at),
                })}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={onShowAgencyVersion}
                >
                  <Eye className="h-4 w-4" />
                  {t('legalSettings.member.viewAgencyVersion')}
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={onMarkReviewed}>
                  <CheckCircle2 className="h-4 w-4" />
                  {t('legalSettings.member.markReviewed')}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <RichTextEditor
          value={isOverridden ? override : agencyValue}
          onChange={onChange}
          readOnly={!isOverridden}
          className={DOCUMENT_EDITOR_CLASS}
        />
        <CardSaveButton {...save} />
      </CardContent>
    </Card>
  );
};

export default LegalPage;
